import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import Spinner from "../../../../../components/Spinner";
import ManagerStatementService from "../../../../../services/managerStatement.service";
import { StatementDocumentApi } from "../../../../../interfaces/managerStatement.interface";

interface ManagerStatementDocumentProps {
  statementId: string;
}

const ManagerStatementDocument: React.FC<ManagerStatementDocumentProps> = ({ statementId }) => {
  const { t } = useTranslation();
  const viewerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [statementDocument, setStatementDocument] = useState<StatementDocumentApi | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const isMobileOrTablet = /Mobi|Android|Tablet|iPad/i.test(navigator.userAgent);

  useEffect(() => {
    if (!statementId) return;

    setLoading(true);
    ManagerStatementService.getStatementDocument(statementId)
      .then((response: any) => {
        setStatementDocument(response ?? null);
        setLoading(false);
      })
      .catch(() => {
        setStatementDocument(null);
        setLoading(false);
      });
  }, [statementId]);

  useEffect(() => {
    if (loading) return;

    const base64 = statementDocument?.pdf_base64;

    if (!base64) {
      setPdfUrl(null);
      if (viewerRef.current) viewerRef.current.innerHTML = "";
      return;
    }

    let objectUrl: string | null = null;

    const loadPdf = async () => {
      try {
        const blob = await fetch(`data:application/pdf;base64,${base64}`).then((res) => res.blob());
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);

        if (!isMobileOrTablet && viewerRef.current) {
          viewerRef.current.innerHTML = `
            <embed
              src="${objectUrl}#navpanes=0&zoom=100"
              type="application/pdf"
              width="100%"
              height="600px"
            />
          `;
        }
      } catch (error) {
        setPdfUrl(null);
      }
    };

    loadPdf();

    return () => {
      if (viewerRef.current) viewerRef.current.innerHTML = "";
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [statementDocument, isMobileOrTablet, loading]);

  const handleDownload = () => {
    if (!pdfUrl) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = statementDocument?.file_name || "manager-statement.pdf";
    link.click();
  };

  return (
    <div className="m-6">
      <div className="card bg-white">
        {loading ? (
          <div className="card-body text-center">
            <Spinner />
          </div>
        ) : !statementDocument?.has_selection ? (
          <div className="card-body text-center text-muted py-6" data-testid="manager-statement-document-empty">
            {t("reconciliation.managerStatement.detail.pageTexts.documentsTab.managerStatement.emptyState")}
          </div>
        ) : (
          <>
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="fw-semibold">{statementDocument.file_name}</div>
                <div className="text-muted small">
                  {t("reconciliation.managerStatement.detail.pageTexts.documentsTab.managerStatement.itemCount", { count: statementDocument.item_count })}
                </div>
              </div>
              <Button
                className="btn btn-primary"
                text={t("reconciliation.managerStatement.detail.pageTexts.documentsTab.managerStatement.downloadButton")}
                onClick={handleDownload}
              />
            </div>
            <div className="card-body p-0">
              {isMobileOrTablet ? (
                pdfUrl && (
                  <Button
                    className="btn btn-primary m-3"
                    text={t("reconciliation.managerStatement.detail.pageTexts.documentsTab.managerStatement.openPdf")}
                    onClick={() => window.open(pdfUrl, "_blank")}
                  />
                )
              ) : (
                <div ref={viewerRef} style={{ width: "100%", height: "600px" }} />
              )}
            </div>
            <div className="card-footer text-muted small">
              {t("reconciliation.managerStatement.detail.pageTexts.documentsTab.managerStatement.infoNote")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManagerStatementDocument;
